import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useCompanyList } from './useCompanyList';
import { CompanyCard } from './CompanyCard';
import styled from '@emotion/styled';
import IconArrowDown from 'ui/images/form/icon_arrow_down_white.svg';
import IconSearch from 'ui/images/form/search.svg';
import { AboutService } from 'ui/components/company/features/AboutService';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';
import { Pagination } from 'ui/components/agent/features/Pagination/Pagination';
import { mqUser } from 'utils/responsive';

const Wrapper = styled.div`
    padding: 18px 50px;
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    gap: 18px 25px;
    align-content: flex-start;
    align-items: flex-start;

    margin: 0 auto;
    background-color: #f8f9fd;
    min-height: 100svh;

    @keyframes fadeIn {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    animation: ${({ loading }: { loading: boolean }) => (loading ? 'none' : 'fadeIn 0.2s ease-in-out')};

    ${mqUser('SP')} {
        padding: 18px 15px;
    }
`;

const SearchWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    margin-bottom: 20px;
`;

const SearchFormWrapper = styled.div<{ isOpen: boolean }>`
    width: 100%;
    display: flex;
    background-color: var(--main-color-blue);
    margin: 0 auto;
    border-radius: 8px;
    color: var(--white);
    padding: 15px 0;
    font-weight: var(--font-bold);
    cursor: pointer;

    img:nth-of-type(1) {
        width: 18px;
        cursor: pointer;
        margin-right: 5px;
        margin-left: 20px;
    }
    img:nth-of-type(2) {
        margin-left: auto;
        margin-right: 25px;
        width: 20px;
        cursor: pointer;
        transition: transform 0.3s ease;
        transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    }
`;

const SearchArea = styled.div<{ isOpen: boolean }>`
    width: 100%;
    background-color: var(--white);
    padding: 20px 0px;
    border-radius: 8px;
    margin-bottom: 10px;
    gap: 20px;
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
`;

const OptionItems = styled.div`
    display: flex;
    gap: 0.625rem 1rem;
    margin-top: 10px;
    flex-wrap: wrap;
`;

const OptionItem = styled.label`
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    font-size: var(--font-medium);

    input {
        margin-right: 5px;
        appearance: auto !important;
    }
`;

const Item = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 0 20px;
`;

const Title = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);

    position: relative;
    padding-left: 10px;
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        width: 4px;
        height: 100%;
        background-color: var(--main-color-blue);
        transform: translateY(-50%);
        border-radius: 2px;
    }
`;

const SearchButton = styled.button`
    width: 300px;
    background-color: var(--primary);
    color: var(--white);
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    padding: 10px;
    border-radius: 25px;
    cursor: pointer;
    margin: 0 auto;
    border: none;
    outline: none;
`;

const CompanyCardContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px 25px;
    margin: 0 auto;
    width: 100%;
`;

const tabs = ['オファーについて', 'プレミアムオファー'];

const descriptions: { [key: string]: string[] } = {
    オファーについて: [
        '気になる案件にオファーを送りましょう。売り手企業がオファーを承諾すると、企業名などの詳細情報と個別のチャットが開通されます。',
        '売り手企業がオファーを承諾し、情報開示＆チャットの開通時に1件あたり5万円が課金（未承諾なら無料）されます。初期費用・月額費用は一切なく、完全成果課金型のリード提供を行います。',
    ],
    プレミアムオファー: [
        'プレミアムオファーを行うと、以下特典があります。',
        '１．売り手企業がMAnyにログインした際にポップアップ通知が表示されます。',
        '２．マッチング後も、売り手企業の受信BOX内で2週間上位に表示され、他のDMより発見性・開封率が高くなります。',
        'プレミアムオファーの送信には1件20,000円（税抜き）が課金され、この金額はオファーが未成立の場合でも返金は行われません。',
    ],
};

export const CompanyList: React.FC = () => {
    const {
        loading,
        companies,
        areaChoices,
        industryChoices,
        employeeChoices,
        revenueChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        setValue,
        submit,
        control,
        currentPage,
        hasNextPage,
        handlePreviousPage,
        handleNextPage,
        handlePageChange,
    } = useCompanyList();
    const [isOpen, setIsOpen] = useState(false);

    const toggleSearchArea = () => {
        setIsOpen(!isOpen);
    };

    return (
        <Wrapper loading={loading}>
            <PageTitle title="売り手企業一覧" />
            <AboutService tabs={tabs} descriptions={descriptions} />
            {!loading && (
                <>
                    <SearchWrapper>
                        <SearchFormWrapper onClick={toggleSearchArea} isOpen={isOpen}>
                            <img src={IconSearch} alt="search" />
                            絞り込み検索
                            <img src={IconArrowDown} alt="arrow-down" />
                        </SearchFormWrapper>
                        <SearchArea isOpen={isOpen}>
                            <Item>
                                <Title>業種</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="industry"
                                        render={({ field }) => (
                                            <>
                                                {industryChoices.map((industry) => (
                                                    <OptionItem key={industry.id}>
                                                        <input
                                                            type="checkbox"
                                                            value={industry.id}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(String(industry.id))
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = String(e.target.value);
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue) // チェックを外す場合
                                                                        : [...field.value, newValue] // チェックを追加する場合
                                                                    : [newValue];
                                                                field.onChange(updatedValue); // react-hook-form の field に更新された値をセット
                                                                setValue('industry', updatedValue); // 選択された industry の ID を industry にセット
                                                            }}
                                                        />
                                                        {industry.name}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>
                            <Item>
                                <Title>エリア</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="area"
                                        render={({ field }) => (
                                            <>
                                                {areaChoices.map(([value, label]) => (
                                                    <OptionItem key={value}>
                                                        <input
                                                            type="checkbox"
                                                            value={value}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(value)
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue)
                                                                        : [...field.value, newValue]
                                                                    : [newValue];
                                                                field.onChange(updatedValue);
                                                                setValue('area', updatedValue);
                                                            }}
                                                        />
                                                        {label}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>
                            <Item>
                                <Title>従業員規模</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="number_of_employees"
                                        render={({ field }) => (
                                            <>
                                                {employeeChoices.map(([value, label]) => (
                                                    <OptionItem key={value}>
                                                        <input
                                                            type="checkbox"
                                                            value={value}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(value)
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue)
                                                                        : [...field.value, newValue]
                                                                    : [newValue];
                                                                field.onChange(updatedValue);
                                                                setValue('number_of_employees', updatedValue);
                                                            }}
                                                        />
                                                        {label}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>
                            <Item>
                                <Title>売上高</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="revenue_scale"
                                        render={({ field }) => (
                                            <>
                                                {revenueChoices.map(([value, label]) => (
                                                    <OptionItem key={value}>
                                                        <input
                                                            type="checkbox"
                                                            value={value}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(value)
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue)
                                                                        : [...field.value, newValue]
                                                                    : [newValue];
                                                                field.onChange(updatedValue);
                                                                setValue('revenue_scale', updatedValue);
                                                            }}
                                                        />
                                                        {label}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>
                            <Item>
                                <Title>営業利益</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="profit_level"
                                        render={({ field }) => (
                                            <>
                                                {profitLevelChoices.map(([value, label]) => (
                                                    <OptionItem key={value}>
                                                        <input
                                                            type="checkbox"
                                                            value={value}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(value)
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue)
                                                                        : [...field.value, newValue]
                                                                    : [newValue];
                                                                field.onChange(updatedValue);
                                                                setValue('profit_level', updatedValue);
                                                            }}
                                                        />
                                                        {label}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>
                            <Item>
                                <Title>純資産金額</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="net_assets_level"
                                        render={({ field }) => (
                                            <>
                                                {netAssetsLevelChoices.map(([value, label]) => (
                                                    <OptionItem key={value}>
                                                        <input
                                                            type="checkbox"
                                                            value={value}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(value)
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue)
                                                                        : [...field.value, newValue]
                                                                    : [newValue];
                                                                field.onChange(updatedValue);
                                                                setValue('net_assets_level', updatedValue);
                                                            }}
                                                        />
                                                        {label}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>
                            <SearchButton onClick={submit}>検索</SearchButton>
                        </SearchArea>
                    </SearchWrapper>
                    <CompanyCardContainer>
                        {companies.map((company) => (
                            <CompanyCard key={company.id} company={company} />
                        ))}
                    </CompanyCardContainer>
                    {companies.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            hasNextPage={hasNextPage}
                            onPreviousPage={handlePreviousPage}
                            onNextPage={handleNextPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            )}
        </Wrapper>
    );
};
